import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Paper,
  Tabs,
  Tab,

  useMediaQuery,
  useTheme,
  Fab,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Button,
  Typography,
  Tooltip,
  IconButton,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { format } from "date-fns";
import {
  exchangeSpecificStringifiedNumber,
 
} from "../../services/exchanges";
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import { useNavigate } from "react-router-dom";
import InstrumentRow from "./InstrumentRow";
import SearchBar from "./SearchBar";
import { useDispatch, useSelector } from "react-redux";
import {
  addMarketWatchInstrumentAction,
  deleteMarketWatchInstrumentAction,
  setInitialTrackIndexAction,
  fetchMarketWatchesAction,
  renameMarketwatchAction,
} from "../../store/actions/watchlist";
import { enqueueSnackbar } from "notistack";
import InstrumentRowSkeleton from './InstrumentRowSkeleton';
import useTouchInteraction from "../../hooks/useTouchInteraction";

// Add TouchableTab component just for better touch handling
const TouchableTab = React.memo(({ label, value, selected, onChange }) => {
  const theme = useTheme();
  const handleClick = useCallback((e) => {
    if (!e) return;
    onChange(e, value);
  }, [onChange, value]);

  const { handlers, touchStates } = useTouchInteraction(handleClick, true);

  return (
    <Tab
      {...handlers}
      label={label}
      value={value}
      onClick={(e) => handleClick(e)}
      sx={{
        height: '56px',
        minHeight: '56px',
        textTransform: 'none',
        fontSize: '0.875rem',
        fontWeight: selected ? 600 : 400,
        opacity: selected ? 1 : 0.7,
        transition: 'all 0.2s ease',
        backgroundColor: touchStates.isActive && !touchStates.isTouchMoved 
          ? 'action.hover' 
            : selected 
              ? theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.08)' 
                : 'rgba(0, 0, 0, 0.04)' 
              : 'transparent',
        color: selected ? theme.palette.primary.main : theme.palette.text.secondary,
        WebkitTapHighlightColor: 'transparent',
        '&:active': {
          backgroundColor: !touchStates.isTouchMoved ? 'action.hover' : 'transparent'
        },
        '&:hover': {
          opacity: 1,
          backgroundColor: theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.12)' 
            : 'rgba(0, 0, 0, 0.08)'
        }
      }}
    />
  );
});

const MarketWatch = ({ onChartOpen }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [activeTab, setActiveTab] = useState(0);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [highlightedInstrument, setHighlightedInstrument] = useState(null);
  // Ref for scrolling to highlighted instrument
  const instrumentListRef = useRef(null);

  // const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedRows, setExpandedRows] = useState([]);
  const { watchlists, watchListIndex, formattedWatchlists : mappedWatchlists} = useSelector(
    (state) => state.marketwatch
  );
  // console.log("mappedWatchlists",mappedWatchlists)
  const [myInstruments, setMyInstruments] = useState([]);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isAtBottom, setIsAtBottom] = useState(false);

  // Rename State
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [editTabName, setEditTabName] = useState("");

  const openRenameDialog = () => {
    setEditTabName(watchlists[watchListIndex]?.name || watchlists[watchListIndex]?.mappedName || "");
    setRenameDialogOpen(true);
  };

  const handleRenameClose = () => {
    setRenameDialogOpen(false);
    setEditTabName("");
  };

  const handleRenameSubmit = async () => {
    if (!editTabName.trim()) {
      enqueueSnackbar("Watchlist name cannot be empty", { 
        variant: "warning",
        anchorOrigin: { vertical: 'top', horizontal: 'right' }
      });
      return;
    }
    const mw_id = watchlists[watchListIndex]?.id;
    if (mw_id) {
      const { status, message } = await dispatch(
        renameMarketwatchAction(mw_id, editTabName.trim(), watchListIndex)
      );
      if (status === "success") {
        enqueueSnackbar("Watchlist renamed successfully", { 
          variant: "success", 
          autoHideDuration: 2000,
          anchorOrigin: { vertical: 'top', horizontal: 'right' }
        });
        handleRenameClose();
      } else {
        enqueueSnackbar(message || "Error renaming watchlist", { 
          variant: "error", 
          autoHideDuration: 2000,
          anchorOrigin: { vertical: 'top', horizontal: 'right' }
        });
      }
    }
  };

  useEffect(() => {
    const currInstrumentByWatchListIndex =
      watchlists[watchListIndex]?.instruments;
    setMyInstruments(currInstrumentByWatchListIndex);
  }, [watchListIndex]);

  // Add visibility change handler to refresh data when tab becomes visible
  useEffect(() => {
    let visibilityTimeout = null;
    let lastRefreshTime = Date.now();
    let wasHidden = false; // Track if tab was previously hidden
    const REFRESH_COOLDOWN = 10000; // 10 seconds minimum between refreshes

    const handleVisibilityChange = () => {
      // Only refresh if coming back from hidden state
      if (document.visibilityState === 'visible' && wasHidden) {
        const currentTime = Date.now();
        const timeInvisible = currentTime - lastRefreshTime;
        
        // If tab was invisible for more than REFRESH_COOLDOWN, schedule a refresh
        if (timeInvisible > REFRESH_COOLDOWN) {
          // Clear any existing timeout
          if (visibilityTimeout) {
            clearTimeout(visibilityTimeout);
          }
          
          // Schedule a single refresh after a short delay to avoid multiple rapid refreshes
          visibilityTimeout = setTimeout(() => {
            console.log('MarketWatch: Refreshing data after tab became visible');
            // Force refresh by passing true parameter
            dispatch(fetchMarketWatchesAction(true));
            lastRefreshTime = Date.now();
            visibilityTimeout = null;
          }, 1000);
        }
        
        // Reset hidden state
        wasHidden = false;
      } else if (document.visibilityState === 'hidden') {
        // Set flag that tab was hidden
        wasHidden = true;
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (visibilityTimeout) {
        clearTimeout(visibilityTimeout);
      }
    };
  }, [dispatch]);

  // Function to scroll to highlighted instrument
  useEffect(() => {
    if (highlightedInstrument && instrumentListRef.current) {
      try {
        // Find the element with the highlighted instrument ID
        const elementId = `instrument-${highlightedInstrument.token}`;
        const element = document.getElementById(elementId);
        
        if (element) {
          // Use our helper function to scroll only within the container
          scrollToHighlighted(element);
          
          // Apply highlight animation via class
          element.classList.add('highlighted-instrument');
          
          // Clear the highlight after a delay
          const timer = setTimeout(() => {
            element.classList.remove('highlighted-instrument');
            setHighlightedInstrument(null);
          }, 3000);
          
          return () => {
            clearTimeout(timer);
            element.classList.remove('highlighted-instrument');
          };
        }
      } catch (error) {
        console.error("Error scrolling to highlighted instrument:", error);
      }
    }
  }, [highlightedInstrument]);

  const addInstrument = async (instrument) => {
    // Check if instrument already exists in current watchlist
    let idx = watchlists[watchListIndex].instruments.findIndex(
      (instr) => instr.trading_symbol === instrument.trading_symbol
    );
    
    // If instrument already exists, highlight it and return
    if (idx !== -1) {
      const existingInstrument = watchlists[watchListIndex].instruments[idx];
      setHighlightedInstrument(existingInstrument);
      setActiveIndex(idx);
      dispatch(setInitialTrackIndexAction(idx));
      enqueueSnackbar(`${instrument.display_name || instrument.trading_symbol} is already in your watchlist`, {
        variant: "info",
        autoHideDuration: 2000
      });
      return;
    }

    // Check if watchlist is full
    if (watchlists[watchListIndex].instruments.length === 20) {
      enqueueSnackbar("Switch to other marketwatch to add new instrument", {
        variant: "info",
      });
      return;
    }

    // Add the instrument
    let payload = {
      mw_id: watchlists[watchListIndex].id,
      exchange: instrument.exchange,
      trading_symbol: instrument.trading_symbol,
      token: instrument.token,
      execution: instrument.execution === "SPREAD" ? "SPREAD" : "NORMAL",
    };

    const { message, status } = await dispatch(
      addMarketWatchInstrumentAction(watchListIndex, payload)
    );
    if (status === "success") {
      enqueueSnackbar(message, { variant: "success", autoHideDuration: 1000 });
    } else {
      if (idx !== -1) {
        setActiveIndex(idx);
        dispatch(setInitialTrackIndexAction(idx));
      }
    }
  };

  const deleteInstrument = async (
    marketwatchId,
    watchlistIndex,
    marketWatches,
    instrument
  ) => {
    const payload = {
      marketWatchId: marketwatchId,
      exchange: instrument.exchange,
      token: instrument.token,
      symbolName: instrument.trading_symbol,
      execution: instrument.execution === "SPREAD" ? "SPREAD" : "NORMAL",
    };
    const { status, message } = await dispatch(
      deleteMarketWatchInstrumentAction(
        payload,
        watchlistIndex,
        marketWatches,
        instrument
      )
    );
    if (status === "success") {
      enqueueSnackbar(message, { variant: "success", autoHideDuration: 1000 });
    } else {
      enqueueSnackbar(message, { variant: "error", autoHideDuration: 1000 });
    }
  };

  const formatExpiryDate = (epochTimestamp) => {
    try {
      // Convert epoch to milliseconds if it's in seconds
      const timestamp =
        epochTimestamp.toString().length === 10
          ? epochTimestamp * 1000
          : epochTimestamp;

      return format(new Date(timestamp), "dd MMM yyyy");
    } catch (err) {
      console.error("Error formatting expiry date:", err);
      return epochTimestamp; // Return original value if parsing fails
    }
  };

  const handleRowClick = useCallback((instrument) => {
    setExpandedRows((prev) =>
      prev.includes(instrument.token)
        ? prev.filter((token) => token !== instrument.token)
        : [...prev, instrument.token]
    );
  }, []);

 

  const handleMore = (instrument, e) => {
    e.stopPropagation();
    // TODO: Implement more options
  };

  // Scroll to highlighted instrument without affecting container position
  const scrollToHighlighted = (element) => {
    if (!element || !instrumentListRef.current) return;
    
    const containerRect = instrumentListRef.current.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    
    // Calculate position within the scrollable container
    const elementTop = elementRect.top - containerRect.top + instrumentListRef.current.scrollTop;
    const containerVisibleHeight = instrumentListRef.current.clientHeight;
    
    // Add a safety margin to ensure the element doesn't go under the search bar
    const safetyMargin = isMobile ? 60 : 10;
    
    // Scroll to center the element in the visible area with safety margin
    instrumentListRef.current.scrollTo({
      top: Math.max(0, elementTop - (containerVisibleHeight / 2) + (elementRect.height / 2) - safetyMargin),
      behavior: 'smooth'
    });
  };

  // Add function to scroll to bottom with smooth animation
  const scrollToBottom = () => {
    if (instrumentListRef.current) {
      // Add extra padding to ensure last item is fully visible
      const extraPadding = 20;
      const maxScrollPosition = instrumentListRef.current.scrollHeight - instrumentListRef.current.clientHeight + extraPadding;
      
      instrumentListRef.current.scrollTo({
        top: Math.max(0, maxScrollPosition),
        behavior: 'smooth'
      });
    }
  };

  // Add function to scroll to top with smooth animation
  const scrollToTop = () => {
    if (instrumentListRef.current) {
      instrumentListRef.current.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };

  // Enhanced scroll event handling for mobile
  useEffect(() => {
    const handleScroll = () => {
      if (instrumentListRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = instrumentListRef.current;
        
        // Check if we're at the bottom (with a larger buffer for better UX)
        const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
        const bottom = distanceFromBottom <= 50; // Increased buffer for better detection
        setIsAtBottom(bottom);
      }
    };

    const listElement = instrumentListRef.current;
    if (listElement) {
      // Use passive listeners for better performance on mobile
      listElement.addEventListener('scroll', handleScroll, { passive: true });
      
      // Add touch event optimization for mobile
      const isMobileDevice = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      if (isMobileDevice) {
        // Optimize touch scrolling
        listElement.addEventListener('touchstart', () => {
          listElement.style.webkitOverflowScrolling = 'touch';
        }, { passive: true });
        
        listElement.addEventListener('touchend', () => {
          // Maintain momentum scrolling
          setTimeout(() => {
            if (listElement) {
              listElement.style.webkitOverflowScrolling = 'touch';
            }
          }, 100);
        }, { passive: true });
      }
      
      return () => {
        listElement.removeEventListener('scroll', handleScroll);
        if (isMobileDevice) {
          listElement.removeEventListener('touchstart', () => {});
          listElement.removeEventListener('touchend', () => {});
        }
      };
    }
  }, []);

  // Reset scroll position when switching between marketwatches
  useEffect(() => {
    // Smooth scroll to top when watchlist changes
    if (instrumentListRef.current) {
      const isMobileDevice = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      if (isMobileDevice) {
        // For mobile, use immediate scroll to avoid animation conflicts
        instrumentListRef.current.scrollTop = 0;
        instrumentListRef.current.scrollTo({ top: 0, behavior: 'auto' });
      } else {
        // For desktop, use smooth scroll
        instrumentListRef.current.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }
    }
  }, [watchListIndex]);

  // iPhone-specific scroll positioning fix with smoother resets
  useEffect(() => {
    const isIPhone = /iPhone/.test(navigator.userAgent);
    
    if (isIPhone && instrumentListRef.current) {
      // Force scroll position reset on mount and data load
      const resetScrollPosition = () => {
        if (instrumentListRef.current) {
          // Multiple reset methods for reliability
          instrumentListRef.current.scrollTop = 0;
          instrumentListRef.current.scrollTo({ top: 0, behavior: 'auto' });
          
          // Force hardware acceleration reset
          const element = instrumentListRef.current;
          element.style.transform = 'translate3d(0, 0, 0)';
          element.style.webkitTransform = 'translate3d(0, 0, 0)';
        }
      };

      // Progressive resets with optimized timing for iPhone
      resetScrollPosition();
      requestAnimationFrame(resetScrollPosition);
      setTimeout(resetScrollPosition, 50);
      setTimeout(resetScrollPosition, 150);
      setTimeout(resetScrollPosition, 300);
    }
  }, [watchlists]); // Reset when data loads

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'background.paper',
        position: 'relative',
        overflow: 'hidden',
        [theme.breakpoints.down('sm')]: {
          height: '100vh',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1,
        }
      }}
    >
      {/* Search Bar - Fixed at Top */}
      {!watchlists[watchListIndex]?.isDefault && (
        <Box
          sx={{
            width: '100%',
            backgroundColor: 'background.paper',
            borderBottom: 1,
            borderColor: 'divider',
            zIndex: 1100,
            position: 'sticky',
            top: 0,
            flexShrink: 0,
            [theme.breakpoints.down('sm')]: {
              position: 'fixed',
              top: '86px', // 56px (app bar) + 30px (top ticker)
              left: 0,
              right: 0,
              height: '48px',
              zIndex: 1100,
              borderBottom: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }
          }}
          role="search"
        >
          <SearchBar
            marketWatches={watchlists}
            activeTab={watchListIndex}
            error={error}
            setError={setError}
            addInstrument={addInstrument}
            setHighlightedInstrument={setHighlightedInstrument}
            currentWatchlist={watchlists[watchListIndex]}
          />
        </Box>
      )}

      {/* Scrollable Content Area - Takes remaining space */}
      <Box 
        sx={{ 
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          overflow: 'hidden',
          minHeight: 0, // Important for flex child
          [theme.breakpoints.down('sm')]: {
            position: 'absolute',
            top: watchlists[watchListIndex]?.isDefault ? '86px' : '134px', // 86px when no search bar
            left: 0,
            right: 0,
            bottom: '112px', // 56px (tabs) + 56px (bottom nav)
            overflow: 'hidden',
            height: watchlists[watchListIndex]?.isDefault ? 'calc(100vh - 198px)' : 'calc(100vh - 246px)'
          }
        }}
      >
        {/* NEW HEADER ROW */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          px: 2, 
          py: 0.5, 
          borderBottom: `1px solid ${theme.palette.divider}`,
          bgcolor: 'background.paper',
          flexShrink: 0,
        }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
            {watchlists[watchListIndex]?.name || watchlists[watchListIndex]?.mappedName || "Marketwatch"}
          </Typography>
          {!watchlists[watchListIndex]?.isDefault ? (
            <Tooltip title="Rename Watchlist">
              <IconButton size="small" onClick={openRenameDialog}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Expand Watchlist">
              <IconButton size="small" onClick={() => navigate('/marketwatch-expanded')}>
                <OpenInFullIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Instruments List - Constrained Container */}
        <Box
          ref={instrumentListRef}
          sx={{
            width: '100%',
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            WebkitOverflowScrolling: 'touch',
            '-webkit-overflow-scrolling': 'touch',
            touchAction: 'pan-y',
            position: 'relative',
            minHeight: 0, // Important for flex child
            // Enhanced mobile scrolling properties
            scrollBehavior: 'smooth',
            overscrollBehavior: 'contain',
            '-webkit-transform': 'translate3d(0,0,0)', // Force hardware acceleration
            transform: 'translate3d(0,0,0)',
            willChange: 'scroll-position',
            // Mobile-specific smooth scrolling
            [theme.breakpoints.down('sm')]: {
              scrollBehavior: 'auto', // Better for touch on mobile
              '-webkit-overflow-scrolling': 'touch',
              '-webkit-transform': 'translateZ(0)', // iOS smooth scrolling
              backfaceVisibility: 'hidden',
              perspective: 1000,
              touchAction: 'pan-y',
              overscrollBehaviorY: 'contain',
              scrollbarWidth: 'none', // Hide scrollbar on mobile
              msOverflowStyle: 'none',
              '&::-webkit-scrollbar': {
                display: 'none' // Hide scrollbar on mobile webkit
              }
            },
            // Desktop scrollbar styling
            [theme.breakpoints.up('md')]: {
              '&::-webkit-scrollbar': {
                width: '4px'
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                borderRadius: '4px'
              }
            }
          }}
        >
          {/* Content with proper spacing */}
          <Box sx={{
            padding: '8px 0',
            minHeight: 'calc(100% + 100px)', // Ensure container is taller than viewport
            [theme.breakpoints.up('sm')]: {
              paddingBottom: '120px' // Increased space for tabs on desktop
            },
            [theme.breakpoints.down('sm')]: {
              paddingBottom: '80px' // Increased bottom padding for mobile
            }
          }}>
            {watchlists.length === 0 ? (
              <InstrumentRowSkeleton count={12} />
            ) : watchlists[watchListIndex]?.instruments?.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                <Typography variant="body1">
                  {watchlists[watchListIndex]?.isDefault 
                    ? "No active stocks available at the moment." 
                    : "No instruments found in this watchlist. Search and add instruments."}
                </Typography>
              </Box>
            ) : (
              watchlists[watchListIndex]?.instruments.map((instrument) => (
                <InstrumentRow
                  key={instrument.token}
                  instrument={instrument}
                  handleMore={handleMore}
                  expandedRows={expandedRows}
                  setExpandedRows={setExpandedRows}
                  handleRowClick={handleRowClick}
                  formatExpiryDate={formatExpiryDate}
                  marketwatchId={watchlists[watchListIndex].id}
                  exchangeSpecificStringifiedNumber={exchangeSpecificStringifiedNumber}
                  marketWatches={watchlists}
                  deleteInstrument={deleteInstrument}
                  activeTab={activeTab}
                  error={error}
                  setError={setError}
                  watchListIndex={watchListIndex}
                  isHighlighted={highlightedInstrument?.token === instrument.token}
                  id={`instrument-${instrument.token}`}
                />
              ))
            )}
          </Box>
        </Box>
      </Box>
        
      {/* Scroll Button */}
      {isMobile && (
        <Fab 
          size="small" 
          color="primary" 
          aria-label={isAtBottom ? "scroll to top" : "scroll to bottom"}
          onClick={isAtBottom ? scrollToTop : scrollToBottom}
          sx={{
            position: 'fixed',
            bottom: '130px',
            right: 16,
            zIndex: 1150,
            boxShadow: 4,
            opacity: 0.95,
            '&:hover': {
              opacity: 1
            }
          }}
        >
          {isAtBottom ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </Fab>
      )}

      {/* MarketWatch Tabs - Fixed at Bottom */}
      <Box
        sx={{
          width: '100%',
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          zIndex: 1200,
          flexShrink: 0,
          height: '56px',
          minHeight: '56px',
          [theme.breakpoints.up('sm')]: {
            position: 'sticky',
            bottom: 0,
          },
          [theme.breakpoints.down('sm')]: {
            position: 'fixed',
            bottom: '56px', // Above bottom navigation
            left: 0,
            right: 0,
            height: '56px'
          }
        }}
      >
        <Tabs
          value={watchListIndex || 0}
          onChange={(_event, selectedId) => {
            dispatch(setInitialTrackIndexAction());
            dispatch({
              type: "SET_WATCHLISTINDEX",
              payload: selectedId,
            });
          }}
          variant="scrollable"
          scrollButtons={true}
          allowScrollButtonsMobile
          sx={{
            height: '56px',
            minHeight: '56px',
            backgroundColor: 'transparent',
            "& .MuiTabs-scrollButtons": {
              opacity: 1,
              color: theme.palette.text.header,
              "&.Mui-disabled": {
                opacity: 0.3,
              },
            },
            "& .MuiTabs-indicator": {
              backgroundColor: theme.palette.primary.main,
              height: 3,
            },
          }}
        >
          {mappedWatchlists?.map((mw, index) => (
            <TouchableTab
              key={mw.id}
              label={mw.mappedName}
              value={index}
              selected={watchListIndex === index}
              onChange={(_event, selectedId) => {
                dispatch(setInitialTrackIndexAction());
                dispatch({
                  type: "SET_WATCHLISTINDEX",
                  payload: mw.id,
                });
              }}
            />
          ))}
        </Tabs>
      </Box>

      {/* Rename Dialog (Desktop & Mobile) */}
      <Dialog open={renameDialogOpen} onClose={handleRenameClose} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 600 }}>Rename Watchlist</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Watchlist Name"
            type="text"
            fullWidth
            variant="outlined"
            value={editTabName}
            onChange={(e) => setEditTabName(e.target.value.substring(0, 15))}
            onKeyDown={(e) => {
              
              if (e.key === 'Enter') handleRenameSubmit();
            }}
            inputProps={{ maxLength: 15 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleRenameClose} color="inherit">Cancel</Button>
          <Button onClick={handleRenameSubmit} variant="contained" color="primary">Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MarketWatch;
